import React from "react";
import { Link } from "react-router-dom";
import { SERVICES, PRICING_DISCLAIMER } from "@/lib/api";
import { useRegion, priceFor } from "@/contexts/RegionContext";
import {
  Sofa,
  Trees,
  HardHat,
  PackageOpen,
  Refrigerator,
  DoorOpen,
  CloudLightning,
  Container,
  CheckCircle2,
  ArrowRight,
  Phone,
  Camera,
} from "lucide-react";

const ICONS = { Sofa, Trees, HardHat, PackageOpen, Refrigerator, DoorOpen, CloudLightning, Container };

const DETAIL = {
  "household-junk": ["Furniture, boxes, electronics", "Attic, basement & garage cleanouts", "Estate & downsizing hauling"],
  "yard-debris": ["Branches, brush, palm fronds", "Post-mowing bag pickup", "Small land clearing debris"],
  "construction-debris": ["Remodel & demo waste", "Drywall, wood, tile, roofing", "Contractor overflow hauling"],
  "bulk-pickup": ["Mattresses, couches, recliners", "Sheds, playsets, hot tubs", "Anything the city won't take"],
  "appliance-removal": ["Refrigerators, freezers, ACs", "Washers, dryers, dishwashers", "Water heaters, stoves, microwaves"],
  "eviction-cleanout": ["Full residential cleanouts", "Landlord & PM turnover crews", "Fast 24–48 hr turnaround"],
  "storm-cleanup": ["Hurricane & tornado debris", "Fallen limbs, fencing, roof scrap", "Documentation photos on request"],
  "dumpster-rental": ["10/20/30-yard roll-off referrals", "Vetted local dumpster partners", "Delivered & picked up on schedule"],
};

const Services = () => {
  const { region } = useRegion();
  return (
    <div data-testid="services-page">
      <section className="border-b border-border bg-muted">
        <div className="container-wide py-16 lg:py-20">
          <p className="kicker">Services &amp; Starting Prices</p>
          <h1 className="mt-3 font-display text-4xl font-black sm:text-5xl lg:text-6xl">
            Every kind of hauling we do.
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Free quotes and upfront pricing on every job. Pick a service to see what&apos;s included.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-wide">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Sofa;
              return (
                <div
                  id={s.slug}
                  key={s.slug}
                  className="group flex scroll-mt-24 flex-col rounded-sm border border-border bg-card p-6 transition-shadow hover:shadow-md"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-secondary text-secondary-foreground">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="rounded-sm bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                      From ${priceFor(s, region)}
                    </div>
                  </div>
                  <h2 className="mt-4 font-display text-xl font-black leading-tight">{s.title}</h2>
                  <p className="mt-2 text-sm text-muted-foreground">{s.short}</p>
                  <ul className="mt-4 space-y-1.5">
                    {DETAIL[s.slug]?.map((d) => (
                      <li key={d} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[hsl(145,27%,34%)]" />
                        <span className="text-foreground/85">{d}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    data-testid={`service-card-cta-${s.slug}`}
                  >
                    Get a quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="mt-8 max-w-3xl text-xs text-muted-foreground">{PRICING_DISCLAIMER}</p>

          <div className="mt-16 grid gap-6 rounded-sm border border-border bg-[hsl(145,27%,24%)] p-8 text-white sm:grid-cols-3 sm:p-10">
            <div className="sm:col-span-2">
              <p className="kicker text-primary">Ready to book?</p>
              <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl">
                Send photos for an exact quote.
              </h2>
              <p className="mt-3 max-w-xl text-white/80">
                Skip the guesswork — a couple photos and a quick description gets you a firm price the same day.
              </p>
            </div>
            <div className="flex flex-col justify-center gap-3">
              <Link to="/contact" className="btn-cta" data-testid="services-bottom-quote-cta">
                <Camera className="h-4 w-4" /> Get a Free Quote
              </Link>
              <a href="tel:+18135550100" className="btn-ghost-dark" data-testid="services-bottom-call-cta">
                <Phone className="h-4 w-4" /> Call Us Instead
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
